/**
 * For Second Shooters Landing Page — SEO-optimized page targeting freelance
 * photographers and assistants looking for second shooting work
 */
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { NavBar } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import {
  ArrowRight, Check, Camera, Star, MapPin, Calendar, FileText,
  Quote, Briefcase, Eye, Award, ChevronDown,
} from "lucide-react";
import React from "react";

var SECOND_FAQ = [
  {
    q: "Is GottaSecond really free for second shooters?",
    a: "Yes — 100% free to create a profile, get discovered by studios, receive job offers, and get hired. There are no hidden fees. You only pay if you choose to upgrade to Pro ($9/month) for features like browsing all job listings and messaging studios directly.",
  },
  {
    q: "How do I get found by studios?",
    a: "Complete your profile with your portfolio, gear list, skills, rates, and travel radius. When studios in your area post jobs, GottaSecond's matching algorithm automatically surfaces your profile based on fit. The more complete your profile, the better your match score.",
  },
  {
    q: "What kind of jobs are on GottaSecond?",
    a: "Primarily weddings and events — the bread and butter of second shooting. Studios post jobs looking for second photographers, second videographers, photo/video assistants, and drone operators. Each listing includes the event date, location, pay rate, and specific requirements.",
  },
  {
    q: "Can I bring reviews from studios I've worked with outside GottaSecond?",
    a: "Yes! You can request reviews from anyone via email — even studios that don't have a GottaSecond account. They can leave a review without signing up, which goes on your profile to help you get hired more often.",
  },
  {
    q: "How do contracts work?",
    a: "When a studio hires you, a contract is automatically generated with all the job details, rates, and terms. You review and sign electronically — no printing, scanning, or chasing paperwork. Everything is stored in your account.",
  },
  {
    q: "What areas does GottaSecond cover?",
    a: "GottaSecond is available nationwide across the United States. Set your location and travel radius, and you'll be matched to jobs within your range.",
  },
];

var SECOND_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Find Second Shooter & Photography Assistant Jobs | GottaSecond",
  "description": "Get hired as a second shooter or photography assistant. Free to join — build your profile, get matched to jobs, and grow your reputation with verified reviews.",
  "url": "https://gottasecond.com/for-second-shooters",
  "isPartOf": {
    "@type": "WebSite",
    "name": "GottaSecond",
    "url": "https://gottasecond.com",
  },
  "mainEntity": {
    "@type": "Service",
    "name": "GottaSecond for Second Shooters",
    "description": "Free platform for freelance photographers and videographers to find second shooting jobs, build their reputation, and manage contracts.",
    "provider": {
      "@type": "Organization",
      "name": "GottaSecond",
      "url": "https://gottasecond.com",
    },
    "serviceType": "Photography job marketplace",
    "areaServed": "United States",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Free for second shooters and photography assistants",
    },
  },
};

var FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": SECOND_FAQ.map(function(item) {
    return {
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": { "@type": "Answer", "text": item.a },
    };
  }),
};

function FAQItem({ question, answer }) {
  var [open, setOpen] = React.useState(false);
  return (
    <div className="py-5" data-testid="second-faq-item">
      <button
        onClick={function() { setOpen(!open); }}
        className="w-full flex items-start justify-between text-left gap-4 group"
        aria-expanded={open}
      >
        <h3 className="text-base font-semibold text-white group-hover:text-white/90 transition-colors pr-4">{question}</h3>
        <ChevronDown className={"w-5 h-5 text-white/40 shrink-0 mt-0.5 transition-transform duration-200 " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <p className="mt-3 text-white/50 text-sm leading-relaxed pr-12">{answer}</p>
      )}
    </div>
  );
}

export default function ForSecondShootersPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <SEO
        title="Find Second Shooter & Photography Assistant Jobs"
        description="Get hired as a second shooter or photography assistant — free. Build your profile with a portfolio, gear list, and verified reviews. Get matched to wedding and event jobs in your area. No fees, no commissions."
        path="/for-second-shooters"
        jsonLd={[SECOND_JSONLD, FAQ_JSONLD]}
      />

      <NavBar>
        <Link to="/for-studios" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider" data-testid="nav-for-studios">
          For Studios
        </Link>
        <Link to="/pricing" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider" data-testid="nav-pricing">
          Pricing
        </Link>
        <Link to="/login" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">
          Login
        </Link>
        <Link to="/register?role=assistant">
          <Button className="bg-coral text-white hover:bg-coral/90 rounded-sm font-bold px-6 py-2 uppercase tracking-wide text-sm">
            Join Free
          </Button>
        </Link>
      </NavBar>

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1557165571-d78166a85dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHx3ZWRkaW5nJTIwcGhvdG9ncmFwaGVyJTIwd2l0aCUyMGNhbWVyYSUyMHNob290aW5nJTIwY291cGxlfGVufDB8fHx8MTc3NTc1MDg3MHww&ixlib=rb-4.1.0&q=85"
            alt="Two photographers holding cameras at an event"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-[#0a0a0a]/30" />
        </div>

        <div className="relative z-10 max-w-[1000px] mx-auto px-6 md:px-12">
          <span className="inline-block text-xs uppercase tracking-[0.3em] text-coral font-mono mb-4">For Second Shooters & Assistants</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6" data-testid="second-hero-headline">
            Get Hired for What
            <br />
            <span className="text-white/80">You're Actually Good At</span>
          </h1>
          <p className="text-base md:text-lg text-white/60 leading-relaxed max-w-2xl mb-6">
            Stop competing in Facebook comment sections. Build a real profile with your portfolio, gear, and verified reviews — and let studios come to you.
          </p>

          {/* Free callout */}
          <div className="inline-flex items-center gap-2 bg-coral/10 border border-coral/30 rounded-full px-5 py-2 mb-8">
            <Check className="w-4 h-4 text-coral" />
            <span className="text-coral font-medium text-sm">100% free to join, get matched, and get hired</span>
          </div>

          <div className="flex flex-wrap gap-4">
            <Link to="/register?role=assistant">
              <Button className="bg-coral text-white hover:bg-coral/90 rounded-sm font-bold px-8 py-4 uppercase tracking-wide text-sm" data-testid="second-hero-cta">
                Create Your Free Profile <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20 md:py-24 border-t border-white/5">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-xs uppercase tracking-[0.3em] text-coral font-mono">What You Get</span>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mt-3">
              Everything You Need to Get Booked More
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4 p-5 bg-white/[0.02] rounded-lg border border-white/5">
              <div className="w-10 h-10 bg-coral/10 rounded flex items-center justify-center shrink-0">
                <Eye className="w-5 h-5 text-coral" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Get Discovered</h3>
                <p className="text-white/50 text-sm">Studios in your area find you automatically through our matching algorithm. Complete your profile and let the work come to you.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-white/[0.02] rounded-lg border border-white/5">
              <div className="w-10 h-10 bg-coral/10 rounded flex items-center justify-center shrink-0">
                <Camera className="w-5 h-5 text-coral" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Showcase Your Work</h3>
                <p className="text-white/50 text-sm">One profile with your portfolio, gear list, rates, and experience. No more scattered Instagram links and cloud folders.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-white/[0.02] rounded-lg border border-white/5">
              <div className="w-10 h-10 bg-coral/10 rounded flex items-center justify-center shrink-0">
                <Star className="w-5 h-5 text-coral" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Build Your Reputation</h3>
                <p className="text-white/50 text-sm">Collect verified reviews from studios — even ones outside GottaSecond. Your track record follows you and helps you get hired.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-white/[0.02] rounded-lg border border-white/5">
              <div className="w-10 h-10 bg-coral/10 rounded flex items-center justify-center shrink-0">
                <Briefcase className="w-5 h-5 text-coral" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">See Jobs That Fit</h3>
                <p className="text-white/50 text-sm">Get matched to jobs based on your location, skills, gear, and availability. Only see opportunities that are actually relevant to you.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-white/[0.02] rounded-lg border border-white/5">
              <div className="w-10 h-10 bg-coral/10 rounded flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-coral" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Contracts & Invoicing</h3>
                <p className="text-white/50 text-sm">Every job comes with an electronic contract. No more chasing paperwork or wondering about payment terms.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-white/[0.02] rounded-lg border border-white/5">
              <div className="w-10 h-10 bg-coral/10 rounded flex items-center justify-center shrink-0">
                <Calendar className="w-5 h-5 text-coral" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Calendar Sync</h3>
                <p className="text-white/50 text-sm">Connect your Google Calendar so studios see your real availability. No more back-and-forth about dates.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 md:py-24 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Quote className="w-10 h-10 text-coral/40 mx-auto mb-8" />
          <blockquote className="text-base md:text-lg text-white/70 leading-relaxed italic mb-8" data-testid="second-testimonial-quote">
            "I've worked on hundreds of weddings as an associate/second/assistant for other photographers and the hardest part always felt like finding the work. Facebook was clunky and often lacked details, showing my work was random social media posts that were difficult to track/share... the whole process was a real circus. But now with Gotta Second, I feel like I can easily see what work is out there, what matches my rate/experience, be protected with contracts, and easily invoice/track my billable. I'm way more excited about Gotta Second than I am about the newest cameras or gear."
          </blockquote>
          <div>
            <p className="text-white font-semibold" data-testid="second-testimonial-author">Skyler P.</p>
            <p className="text-white/40 text-sm">Second Shooter & Photo Assistant</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-24 border-t border-white/5">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-center mb-14">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 bg-coral/10 rounded-full flex items-center justify-center mx-auto mb-5">
                <span className="text-xl font-bold text-coral">1</span>
              </div>
              <h3 className="font-semibold mb-2">Build Your Profile</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Add your portfolio, gear, skills, rates, and travel radius. Takes under 5 minutes. The more complete, the better your matches.
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-coral/10 rounded-full flex items-center justify-center mx-auto mb-5">
                <span className="text-xl font-bold text-coral">2</span>
              </div>
              <h3 className="font-semibold mb-2">Get Matched & Hired</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                When studios post jobs in your area, you'll show up as a match. Studios review your profile and send hire offers directly.
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-coral/10 rounded-full flex items-center justify-center mx-auto mb-5">
                <span className="text-xl font-bold text-coral">3</span>
              </div>
              <h3 className="font-semibold mb-2">Shoot & Grow</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Sign contracts electronically, do the job, and collect reviews. Your reputation grows with every gig.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Free vs Pro */}
      <section className="py-20 md:py-24 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-[800px] mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-4">
            Free Forever. Upgrade If You Want More.
          </h2>
          <p className="text-white/50 mb-10">Most second shooters never need to pay a dime.</p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-[#0a0a0a] border border-coral/30 rounded-lg p-8 relative" data-testid="second-price-free">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-coral text-white text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">Most Popular</span>
              <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Basic</p>
              <p className="text-4xl font-bold text-coral mb-1">Free</p>
              <p className="text-white/50 text-sm mb-4">forever</p>
              <ul className="space-y-2 text-white/60 text-sm text-left">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-coral shrink-0" /> Full profile with portfolio & gear</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-coral shrink-0" /> Get discovered by studios</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-coral shrink-0" /> Receive job offers & messages</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-coral shrink-0" /> Sign contracts electronically</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-coral shrink-0" /> Collect verified reviews</li>
              </ul>
            </div>
            <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-8" data-testid="second-price-pro">
              <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Pro</p>
              <p className="text-4xl font-bold mb-1">$9</p>
              <p className="text-white/50 text-sm mb-4">per month</p>
              <ul className="space-y-2 text-white/60 text-sm text-left">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-coral shrink-0" /> Everything in Free</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-coral shrink-0" /> Browse all job listings</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-coral shrink-0" /> Message studios directly</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-coral shrink-0" /> Priority in search results</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-coral shrink-0" /> Email alerts for new jobs</li>
              </ul>
            </div>
          </div>

          <Link to="/register?role=assistant">
            <Button className="bg-coral text-white hover:bg-coral/90 rounded-sm font-bold px-8 py-4 uppercase tracking-wide text-sm" data-testid="second-pricing-cta">
              Join Free <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24 border-t border-white/5" data-testid="second-faq-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 tracking-tight">
            Frequently Asked Questions
          </h2>
          <p className="text-white/50 text-center mb-10 text-sm">
            Common questions from second shooters and assistants
          </p>
          <div className="space-y-0 divide-y divide-white/8">
            {SECOND_FAQ.map(function(item, idx) {
              return <FAQItem key={idx} question={item.q} answer={item.a} />;
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-white/[0.03] border-t border-white/5">
        <div className="max-w-[800px] mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Your Work Deserves a Better Showcase
          </h2>
          <p className="text-white/50 text-base mb-8">
            Create your free profile in under 5 minutes. Let your portfolio speak for itself.
          </p>
          <Link to="/register?role=assistant">
            <Button className="bg-coral text-white hover:bg-coral/90 rounded-sm font-bold px-10 py-4 uppercase tracking-wide text-sm" data-testid="second-final-cta">
              Create Your Free Profile <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
